export type ShippingMethod = {
  id: string
  name: string
  description?: string
  price: number
  defaultChecked?: boolean
}

export type ShippingMethodSource = 'shop' | 'custom'

export const SHIPPING_METHOD_SOURCE_OPTIONS = [
  { value: 'shop', label: 'Store shipping methods (default)' },
  { value: 'custom', label: 'Custom shipping methods' },
] as const

export const DEFAULT_CUSTOM_SHIPPING_METHODS: ShippingMethod[] = [
  {
    id: 'standard',
    name: 'Standard shipping',
    description: '5–7 business days',
    price: 5,
    defaultChecked: true,
  },
  {
    id: 'express',
    name: 'Express shipping',
    description: '2–3 business days',
    price: 15,
    defaultChecked: false,
  },
]

export function isDefaultCheckedValue(value: unknown): boolean {
  return value === true || value === 'true' || value === 1 || value === '1'
}

/** Allow up to 2 decimal places while the merchant types a shipping price. */
export const SHIPPING_PRICE_INPUT_PATTERN = /^\d*(\.\d{0,2})?$/

/** Keep only digits and a single decimal point (max 2 fractional digits). */
export function sanitizeShippingPriceInput(raw: string): string {
  let value = raw.replace(/[^\d.]/g, '')
  const firstDot = value.indexOf('.')
  if (firstDot !== -1) {
    const before = value.slice(0, firstDot + 1)
    const after = value.slice(firstDot + 1).replace(/\./g, '')
    value = before + after
  }
  const match = value.match(/^(\d*)(\.\d{0,2})?/)
  if (!match) return ''
  return `${match[1] ?? ''}${match[2] ?? ''}`
}

export function isPartialShippingPriceInput(raw: string): boolean {
  const trimmed = raw.trim()
  return trimmed === '' || trimmed === '.' || trimmed.endsWith('.')
}

export function isValidShippingPriceInput(raw: string): boolean {
  return SHIPPING_PRICE_INPUT_PATTERN.test(raw.trim())
}

export function normalizeShippingPrice(value: unknown): number {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.round(n * 100) / 100
}

export function parseShippingPriceInput(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '' || isPartialShippingPriceInput(trimmed)) return null
  if (!isValidShippingPriceInput(trimmed)) return null
  const price = Number.parseFloat(trimmed)
  if (!Number.isFinite(price) || price < 0) return null
  return normalizeShippingPrice(price)
}

export function finalizeShippingPriceInput(raw: string): number {
  return parseShippingPriceInput(raw) ?? 0
}

export function formatShippingPriceForInput(price: number | null | undefined): string {
  if (price === 0) return '0'
  if (price == null || !Number.isFinite(price)) return ''
  return String(normalizeShippingPrice(price))
}

/** Normalized shipping method objects (the shape persisted on save). */
export function normalizeCustomShippingMethods(
  methods: ShippingMethod[],
): Array<{
  id: string
  name: string
  description: string
  price: number
  defaultChecked: boolean
}> {
  return methods.map((m) => ({
    id: m.id,
    name: m.name,
    description: m.description ?? '',
    price: normalizeShippingPrice(m.price),
    defaultChecked: Boolean(m.defaultChecked),
  }))
}

export function serializeCustomShippingMethods(methods: ShippingMethod[]): string {
  return JSON.stringify(normalizeCustomShippingMethods(methods))
}

/**
 * Parse the stored custom shipping methods into structured objects.
 * Accepts either a JSON string (legacy storage) or an already-parsed array/object.
 */
export function parseCustomShippingMethods(value: unknown): ShippingMethod[] {
  let raw: unknown = value
  if (typeof value === 'string') {
    const text = value.trim()
    if (!text) return []
    try {
      raw = JSON.parse(text)
    } catch {
      return []
    }
  }
  if (!Array.isArray(raw)) return []
  return raw.map((item, index) => {
    const o = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>
    const amount = o.amount ?? o.price ?? 0
    return {
      id: String(o.id ?? `custom-${index}`),
      name: String(o.label ?? o.name ?? ''),
      description: String(o.description ?? ''),
      price: normalizeShippingPrice(amount),
      defaultChecked: isDefaultCheckedValue(o.defaultChecked),
    }
  })
}

export function getDefaultCustomShippingMethodsSerialized(): string {
  return serializeCustomShippingMethods(DEFAULT_CUSTOM_SHIPPING_METHODS)
}

export function createCustomShippingMethodDraft(
  existing: ShippingMethod[] = [],
): ShippingMethod {
  const ids = new Set(existing.map((m) => m.id))
  let id = `custom-${Date.now()}`
  let n = 0
  while (ids.has(id)) {
    n += 1
    id = `custom-${Date.now()}-${n}`
  }
  return { id, name: '', description: '', price: 0, defaultChecked: false }
}

export function resolveDefaultCustomShippingMethod(
  methods: ShippingMethod[],
): ShippingMethod | undefined {
  if (methods.length === 0) return undefined
  return methods.find((m) => m.defaultChecked) ?? methods[0]
}

export function parseShippingMethodSource(
  value: unknown,
): ShippingMethodSource {
  const v = String(value ?? '').trim().toLowerCase()
  return v === 'custom' ? 'custom' : 'shop'
}

export function isCustomShippingMethodSource(
  content: Record<string, string>,
): boolean {
  return parseShippingMethodSource(content.shippingSource) === 'custom'
}
